export type Aisle =
  | 'Produce'
  | 'Dairy'
  | 'Meat'
  | 'Seafood'
  | 'Bakery'
  | 'Pantry'
  | 'Frozen'
  | 'Beverages'
  | 'Snacks'
  | 'Household'
  | 'Other'

const AISLE_KEYWORDS: Record<Aisle, string[]> = {
  Produce: [
    'apple', 'banana', 'lettuce', 'spinach', 'kale', 'tomato', 'onion',
    'pepper', 'carrot', 'potato', 'avocado', 'lemon', 'lime', 'orange',
    'berry', 'broccoli', 'cucumber', 'garlic', 'herb', 'cilantro', 'basil',
    'parsley', 'celery', 'mushroom', 'salad', 'fruit', 'vegetable', 'produce',
  ],
  Dairy: [
    'milk', 'cheese', 'yogurt', 'butter', 'cream', 'egg',
    'cottage cheese', 'half and half', 'kefir',
  ],
  Meat: [
    'chicken', 'beef', 'pork', 'turkey', 'ham', 'bacon', 'sausage',
    'ground', 'steak', 'lamb', 'meat',
  ],
  Seafood: [
    'salmon', 'tuna', 'shrimp', 'fish', 'cod', 'tilapia', 'crab', 'lobster',
  ],
  Bakery: [
    'bread', 'bagel', 'muffin', 'roll', 'tortilla', 'pita', 'croissant',
    'baguette', 'sourdough', 'cake', 'pastry', 'bakery',
  ],
  Frozen: ['frozen', 'ice cream', 'pizza'],
  Beverages: [
    'juice', 'soda', 'water', 'coffee', 'tea', 'beer', 'wine', 'sparkling',
    'kombucha', 'lemonade', 'beverage',
  ],
  Snacks: [
    'chip', 'cracker', 'cookie', 'pretzel', 'nut', 'popcorn', 'granola bar',
    'candy', 'chocolate', 'snack',
  ],
  Pantry: [
    'rice', 'pasta', 'flour', 'sugar', 'oil', 'vinegar', 'sauce', 'salsa',
    'cereal', 'oats', 'bean', 'lentil', 'soup', 'broth', 'stock', 'salt',
    'spice', 'honey', 'syrup', 'peanut butter', 'jam', 'jelly',
    'condiment', 'ketchup', 'mustard', 'mayo', 'olive oil', 'staple',
  ],
  Household: [
    'paper towel', 'toilet paper', 'detergent', 'soap', 'shampoo',
    'toothpaste', 'sponge', 'trash bag', 'foil', 'plastic wrap',
    'cleaning', 'personal care', 'personal_care', 'household',
  ],
  Other: [],
}

const AISLE_ORDER: Aisle[] = [
  'Produce', 'Meat', 'Seafood', 'Dairy', 'Bakery',
  'Frozen', 'Pantry', 'Beverages', 'Snacks', 'Household', 'Other',
]

/**
 * Map the app's existing `display_category` values (e.g. 'fresh_produce', 'meat_fish',
 * 'pantry_staple', 'personal_care') onto the editorial aisle names.
 */
function normalizeCategory(cat: string): Aisle | null {
  const lower = cat.toLowerCase().replace(/_/g, ' ')
  if (lower.includes('produce') || lower.includes('fruit') || lower.includes('vegetable')) return 'Produce'
  if (lower.includes('dairy')) return 'Dairy'
  // 'meat_fish' is a single conflated category — route it to Meat
  if (lower.includes('meat')) return 'Meat'
  if (lower.includes('seafood') || lower.includes('fish')) return 'Seafood'
  if (lower.includes('bakery') || lower.includes('bread')) return 'Bakery'
  if (lower.includes('frozen')) return 'Frozen'
  if (lower.includes('beverage') || lower.includes('drink')) return 'Beverages'
  if (lower.includes('snack')) return 'Snacks'
  if (lower.includes('pantry') || lower.includes('staple')) return 'Pantry'
  if (lower.includes('cleaning') || lower.includes('household') || lower.includes('personal')) return 'Household'
  if (lower === 'other' || lower === 'misc') return 'Other'
  // Accept a directly-matching aisle name (e.g., stored as 'Produce')
  for (const aisle of AISLE_ORDER) {
    if (aisle.toLowerCase() === lower) return aisle
  }
  return null
}

export function inferAisle(itemName: string, explicitCategory?: string | null): Aisle {
  if (explicitCategory) {
    const fromCategory = normalizeCategory(explicitCategory)
    if (fromCategory) return fromCategory
  }
  const lower = itemName.toLowerCase()
  for (const aisle of AISLE_ORDER) {
    for (const kw of AISLE_KEYWORDS[aisle]) {
      if (lower.includes(kw)) return aisle
    }
  }
  return 'Other'
}

export type GroupedItems<T> = Array<{ aisle: Aisle; items: T[] }>

/**
 * Groups items by inferred aisle, preserving the original order of items within each bucket.
 * Aisles are returned in AISLE_ORDER (Produce first, Other last).
 */
export function groupByAisle<T extends { name: string; display_category?: string | null; category?: string | null }>(
  items: T[],
): GroupedItems<T> {
  const buckets = new Map<Aisle, T[]>()
  for (const item of items) {
    const cat   = item.display_category ?? item.category ?? null
    const aisle = inferAisle(item.name, cat)
    if (!buckets.has(aisle)) buckets.set(aisle, [])
    buckets.get(aisle)!.push(item)
  }
  return AISLE_ORDER
    .filter(a => buckets.has(a))
    .map(aisle => ({ aisle, items: buckets.get(aisle)! }))
}
